/**
 * TOURNAMENT_BUNDLE wizard — wraps N already-saved SQUAD/OFFICIALS/VENUE
 * packs into a single marketplace listing.
 *
 * Founder flow (2026-07-31):
 *   1. Save each child pack as a draft in its own wizard (no publish).
 *   2. Open this wizard, fill bundle details (name, cover blurb, price
 *      for buying the whole bundle at once).
 *   3. Pick the previously-saved SQUAD/OFFICIALS/VENUE drafts that
 *      belong to this tournament.
 *   4. Save draft (bundle stays hidden) OR Save & publish (bundle goes
 *      live, ONE promotion blast fires across WhatsApp + email + in-app
 *      unless test mode is on).
 *
 * Buyer flow:
 *   - Marketplace tile shows "N packs inside".
 *   - Detail page lists each child with its own price; buyer can buy
 *     just one child OR the whole bundle at bundle price.
 *
 * Opens when /admin/marketplace mounts with
 * ?action=new&type=TOURNAMENT_BUNDLE.
 */
import { useEffect, useState } from "react";
import {
  Sparkles, Save, CheckCircle2, Package, Users, Gavel, MapPin,
  Trash2, ChevronUp, ChevronDown,
} from "lucide-react";
import {
  createPack,
  updatePack,
  publishPack,
  listPacks,
  selectBundleItems,
} from "../../api/owner.api";
import PackDetailsForm from "../../components/marketplace/PackDetailsForm";
import WizardShell from "../../components/marketplace/WizardShell";
import { toArray } from "../../utils/toArray";

const TYPE_META = {
  SQUAD_PACK:     { label: "Squad",     Icon: Users,  tone: "bg-blue-500/15 text-blue-300 border-blue-400/30" },
  OFFICIALS_PACK: { label: "Officials", Icon: Gavel,  tone: "bg-sky-500/15 text-sky-300 border-sky-400/30" },
  VENUE_PACK:     { label: "Venues",    Icon: MapPin, tone: "bg-teal-500/15 text-teal-300 border-teal-400/30" },
};

export default function TournamentBundleWizard({ onClose, onCreated }) {
  const [details, setDetails] = useState({
    name: "",
    season: "",
    short_description: "",
    description: "",
    price_credits: 999,
  });
  // Available drafts (across all three child pack types).
  const [available, setAvailable] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(true);
  // Selected child pack IDs, in display order.
  const [pickedIds, setPickedIds] = useState([]);
  // Cache of the pick metadata so we can render the picked list without
  // re-searching.
  const [pickedById, setPickedById] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [testMode, setTestMode] = useState(true);
  const [packId, setPackId] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingAvailable(true);
    Promise.all([
      listPacks({ pack_type: "SQUAD_PACK" }),
      listPacks({ pack_type: "OFFICIALS_PACK" }),
      listPacks({ pack_type: "VENUE_PACK" }),
    ])
      .then((responses) => {
        if (cancelled) return;
        const rows = responses.flatMap((r) => toArray(r.data));
        setAvailable(rows);
      })
      .finally(() => { if (!cancelled) setLoadingAvailable(false); });
    return () => { cancelled = true; };
  }, []);

  function togglePick(pack) {
    const id = String(pack.id);
    setPickedIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
    setPickedById((prev) => ({ ...prev, [id]: pack }));
  }

  function movePicked(id, direction) {
    setPickedIds((prev) => {
      const i = prev.indexOf(id);
      if (i === -1) return prev;
      const j = direction === "up" ? i - 1 : i + 1;
      if (j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  function removePicked(id) {
    setPickedIds((prev) => prev.filter((x) => x !== id));
  }

  function detailsValid() {
    return details.name.trim().length >= 3
      && Number.isFinite(Number(details.price_credits))
      && Number(details.price_credits) >= 0;
  }

  function _packBody() {
    return {
      name: details.name.trim(),
      season: details.season.trim(),
      short_description: details.short_description.trim(),
      description: details.description.trim(),
      price_credits: Number(details.price_credits),
    };
  }

  async function _persist() {
    let pack;
    if (packId) {
      const { data } = await updatePack(packId, _packBody());
      pack = data;
    } else {
      const slug = details.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 80);
      const { data } = await createPack({
        ..._packBody(),
        slug,
        pack_type: "TOURNAMENT_BUNDLE",
      });
      pack = data;
      setPackId(pack.id);
    }
    const items = pickedIds.map((id, position) => ({
      child_id: id,
      position,
    }));
    const { data: summary } = await selectBundleItems(pack.id, items);
    return { pack, summary };
  }

  function _errorMessage(e) {
    return e?.response?.data
      ? (typeof e.response.data === "string"
          ? e.response.data
          : (e.response.data.detail
              || e.response.data.error
              || JSON.stringify(e.response.data)))
      : e.message;
  }

  function _validate() {
    if (!detailsValid()) {
      setError("Fill in name (≥ 3 chars) and a non-negative price first.");
      return false;
    }
    if (pickedIds.length === 0) {
      setError("Pick at least one child pack.");
      return false;
    }
    return true;
  }

  async function handleSaveDraft() {
    if (!_validate()) return;
    setBusy(true);
    setError(null);
    try {
      const { pack } = await _persist();
      setSavedAt(new Date());
      if (onCreated) onCreated(pack);
    } catch (e) {
      setError(_errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function handlePublish() {
    if (!_validate()) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { pack, summary } = await _persist();
      const { data: published } = await publishPack(pack.id, {
        skipPromotion: testMode,
      });
      setResult({
        pack_name: published.name,
        pack_id: pack.id,
        items_created: summary.items_created || 0,
        items: summary.items || [],
        errors: summary.errors || [],
        test_mode: testMode,
      });
      if (onCreated) onCreated(pack);
    } catch (e) {
      setError(_errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const totalChildPrice = pickedIds.reduce((sum, id) => {
    const p = pickedById[id];
    return sum + (Number(p?.price_credits) || 0);
  }, 0);

  const sidebar = result ? (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold uppercase tracking-widest text-text-secondary">
        Tournament bundle live
      </h3>
      <ul className="text-sm text-text-primary space-y-1">
        <li>{result.items_created} child pack{result.items_created === 1 ? "" : "s"} wrapped</li>
        <li className="text-text-secondary">
          {result.test_mode ? "Published in test mode." : "One promotion blast fired."}
        </li>
      </ul>
    </div>
  ) : (
    <>
      <PackDetailsForm
        details={details}
        setDetails={setDetails}
        namePlaceholder="IPL 2026 — Full tournament bundle"
        shortDescriptionPlaceholder="Squads, officials + venues for IPL 2026"
        testMode={testMode}
        setTestMode={setTestMode}
      />

      <div className="pt-3 border-t border-border-default">
        <div className="text-xs uppercase tracking-widest text-text-secondary mb-2">
          Bundle composition
        </div>
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-text-primary font-medium flex items-center gap-1.5">
            <Package className="w-3.5 h-3.5" /> Child packs
          </span>
          <span className="font-mono">{pickedIds.length}</span>
        </div>
        {pickedIds.length > 0 && (
          <div className="text-xs text-text-secondary mt-1">
            À la carte total: <span className="font-mono">{totalChildPrice} ZC</span>
            {Number(details.price_credits) > 0 && Number(details.price_credits) < totalChildPrice && (
              <span className="text-emerald-300 ml-1">
                (bundle saves {totalChildPrice - Number(details.price_credits)} ZC)
              </span>
            )}
          </div>
        )}
      </div>

      {savedAt && (
        <div className="pt-3 border-t border-border-default text-xs text-emerald-300 flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Draft saved · {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      )}
    </>
  );

  return (
    <WizardShell
      eyebrow="Marketplace · TOURNAMENT_BUNDLE"
      title={result ? "Tournament bundle live" : "New tournament bundle"}
      onClose={onClose}
      busy={busy}
      error={error}
      sidebar={sidebar}
      primaryLabel={result ? "Done" : (packId ? "Publish" : "Create & publish")}
      primaryIcon={result ? null : <Sparkles className="w-4 h-4" />}
      primaryDisabled={!result && (!detailsValid() || pickedIds.length === 0)}
      primaryTone="amber"
      onPrimary={result ? onClose : handlePublish}
      secondaryLabel={result ? undefined : (packId ? "Update draft" : "Save draft")}
      secondaryIcon={result ? null : <Save className="w-4 h-4" />}
      secondaryDisabled={!detailsValid() || pickedIds.length === 0}
      onSecondary={handleSaveDraft}
    >
      {!result ? (
        <div className="space-y-6">
          <PickedList
            pickedIds={pickedIds}
            pickedById={pickedById}
            onMove={movePicked}
            onRemove={removePicked}
          />

          <AvailableList
            loading={loadingAvailable}
            packs={available}
            pickedIds={pickedIds}
            onToggle={togglePick}
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-emerald-200 flex items-center gap-2 font-semibold">
            <CheckCircle2 className="w-5 h-5" /> Published “{result.pack_name}”
          </div>
          {result.items.length > 0 && (
            <div className="rounded-md border border-border-default bg-bg-secondary/40">
              <div className="px-3 py-2 text-xs uppercase tracking-wider text-text-secondary border-b border-border-default">
                Packs in this bundle
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {result.items.map((it) => (
                    <tr key={it.child_id} className="border-t border-border-default first:border-t-0">
                      <td className="px-3 py-1.5 font-medium">{it.name}</td>
                      <td className="px-3 py-1.5 text-xs text-text-secondary">
                        {TYPE_META[it.pack_type]?.label || it.pack_type}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-text-secondary text-right">
                        {it.price_credits} ZC
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {result.errors.length > 0 && (
            <details className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-200 text-sm">
              <summary className="cursor-pointer font-medium">
                {result.errors.length} row{result.errors.length === 1 ? "" : "s"} skipped
              </summary>
              <ul className="mt-2 ml-3 space-y-1 text-xs">
                {result.errors.slice(0, 20).map((e, i) => (
                  <li key={i}>
                    {e.child_id ? `${e.child_id.slice(0, 8)}…: ` : ""}{e.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </WizardShell>
  );
}


function PickedList({ pickedIds, pickedById, onMove, onRemove }) {
  if (pickedIds.length === 0) {
    return (
      <section className="rounded-lg border border-dashed border-border-default bg-bg-secondary/30 px-6 py-8 text-center text-sm text-text-secondary">
        No child packs picked yet. Toggle rows below to add them to this bundle.
      </section>
    );
  }
  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold">Bundle contents</h3>
      <ol className="rounded-lg border border-border-default bg-bg-secondary/40 divide-y divide-border-default">
        {pickedIds.map((id, i) => {
          const p = pickedById[id] || {};
          const meta = TYPE_META[p.pack_type] || {};
          const Icon = meta.Icon || Package;
          return (
            <li key={id} className="px-4 py-2.5 flex items-center gap-3">
              <span className="text-xs font-mono text-text-secondary w-6">{i + 1}.</span>
              <span className={`text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${meta.tone || ""} inline-flex items-center gap-1`}>
                <Icon className="w-3 h-3" /> {meta.label || p.pack_type}
              </span>
              <span className="flex-1 truncate text-sm">{p.name || id}</span>
              <span className="text-xs font-mono text-text-secondary">
                {p.price_credits ?? "?"} ZC
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => onMove(id, "up")}
                  disabled={i === 0}
                  className="p-1 rounded border border-border-default text-text-secondary hover:text-text-primary hover:bg-bg-secondary disabled:opacity-30"
                  title="Move up"
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => onMove(id, "down")}
                  disabled={i === pickedIds.length - 1}
                  className="p-1 rounded border border-border-default text-text-secondary hover:text-text-primary hover:bg-bg-secondary disabled:opacity-30"
                  title="Move down"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => onRemove(id)}
                  className="p-1 rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                  title="Remove from bundle"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}


function AvailableList({ loading, packs, pickedIds, onToggle }) {
  const [search, setSearch] = useState("");
  const pickedSet = new Set(pickedIds);
  const q = search.trim().toLowerCase();
  const filtered = q
    ? packs.filter((p) => (p.name || "").toLowerCase().includes(q)
        || (p.season || "").toLowerCase().includes(q))
    : packs;

  // Group by pack_type so the founder sees clear buckets.
  const byType = { SQUAD_PACK: [], OFFICIALS_PACK: [], VENUE_PACK: [] };
  for (const p of filtered) {
    if (byType[p.pack_type]) byType[p.pack_type].push(p);
  }

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-base font-semibold">Available packs</h3>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or season…"
          className="w-64 px-3 py-1.5 text-sm rounded-md border border-border-default bg-bg-primary focus:outline-none focus:border-button-primary"
        />
      </div>

      {loading && (
        <div className="text-sm text-text-secondary">Loading packs…</div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="text-sm text-text-secondary rounded-md border border-dashed border-border-default px-4 py-6 text-center">
          No saved child packs found. Create a Squad / Officials / Venue pack first.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(["SQUAD_PACK", "OFFICIALS_PACK", "VENUE_PACK"]).map((type) => {
          const meta = TYPE_META[type];
          const rows = byType[type];
          const Icon = meta.Icon;
          return (
            <div key={type} className="rounded-lg border border-border-default bg-bg-secondary/30 overflow-hidden">
              <div className="px-3 py-2 border-b border-border-default flex items-center gap-2 text-xs uppercase tracking-wider">
                <Icon className="w-3.5 h-3.5" />
                <span className="font-semibold">{meta.label}</span>
                <span className="ml-auto font-mono text-text-secondary">
                  {rows.length}
                </span>
              </div>
              <ul className="max-h-96 overflow-y-auto divide-y divide-border-default">
                {rows.map((p) => {
                  const picked = pickedSet.has(String(p.id));
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => onToggle(p)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-bg-secondary flex items-baseline justify-between gap-2 ${picked ? "bg-emerald-500/10" : ""}`}
                      >
                        <span className="min-w-0">
                          <span className="font-medium block truncate">{p.name}</span>
                          {p.season && (
                            <span className="text-xs text-text-secondary">{p.season}</span>
                          )}
                        </span>
                        <span className="text-xs font-mono text-text-secondary shrink-0">
                          {p.price_credits} ZC
                        </span>
                      </button>
                    </li>
                  );
                })}
                {rows.length === 0 && (
                  <li className="px-3 py-4 text-xs text-text-secondary text-center italic">
                    None
                  </li>
                )}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}
